import type { SystemInfo } from "@ekidenfi/ts-sdk";
import { getEkidenSdkClient } from "@src/api/sdk";
import { makeAutoObservable, runInAction } from "mobx";

interface ExtendedSystemInfo extends Partial<SystemInfo> {
  build_id: string;
  aptos_network: string;
  perpetual_addr: string;
  quote_asset_metadata?: string;
}

export class ConfigStore {
  isInitialized = false;
  isLoading = true;
  hasError = false;
  config: ExtendedSystemInfo | null = null;
  /**
   * True only after both the config is fetched AND the SDK client has been
   * fully reset / reconfigured with the correct URLs and vault address.
   * Reactions that depend on a ready SDK (WS connection, order loading, …)
   * must observe this flag instead of `isInitialized` / `isConfigAvailable`
   * to avoid racing against `resetEkidenSdkClient()`.
   */
  isSdkReady = false;
  private isFetching = false;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchConfig() {
    if (this.isFetching || this.isInitialized) {
      return;
    }
    this.isFetching = true;

    runInAction(() => {
      this.isLoading = true;
      this.hasError = false;
    });

    try {
      const data = await getEkidenSdkClient().system.getSystemInfo();
      runInAction(() => {
        this.config = data;
        this.isInitialized = true;
        this.isLoading = false;
        this.hasError = false;
      });
    } catch {
      runInAction(() => {
        this.isInitialized = true;
        this.isLoading = false;
        this.hasError = true;
      });
    }
  }

  setSdkReady = (value: boolean) => {
    this.isSdkReady = value;
  };

  get isConfigAvailable() {
    return this.config !== null;
  }

  get vaultAddress() {
    return this.config?.perpetual_addr || "";
  }

  get aptosNetwork() {
    return this.config?.aptos_network || "testnet";
  }

  get aptosApiKey() {
    return this.config?.aptos_api_key || "";
  }

  get gasStationApiKey() {
    return this.config?.gas_station_api_key || "";
  }

  get quoteAssetMetadata() {
    return this.config?.quote_asset_metadata || "";
  }
}
